import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, parseErrorDetail } from "../lib/api";

// Fields displayed / accepted on Admin view — the R23 whitelist:
//   Product, Backorder Qty, Delivered Qty, Remaining Qty,
//   Delivery Date, Xero Invoice Ref (+ correction / reversal reason).
// NO price, GST, payment, Reward, supplier, delay, ETA — enforced by the
// server response schema.

const todayIso = () => new Date().toISOString().slice(0, 10);
const uuid = () => (window.crypto && window.crypto.randomUUID
  ? window.crypto.randomUUID()
  : "idem-" + Math.random().toString(36).slice(2) + Date.now());

// ---------------------------------------------------------------- Create modal
function CreateBackorderModal({ apUsers, products, onClose, onDone }) {
  const [apUserId, setApUserId] = useState(apUsers[0]?.id || "");
  const [lines, setLines] = useState([{ product_id: "", opening_qty: "" }]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const updateLine = (i, k, v) => setLines((prev) =>
    prev.map((l, ix) => (ix === i ? { ...l, [k]: v } : l))
  );
  const addLine = () => setLines((prev) => [...prev, { product_id: "", opening_qty: "" }]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, ix) => ix !== i));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!apUserId) { setErr("Choose the AP account."); return; }
    for (const l of lines) {
      if (!l.product_id) { setErr("Every line must select a product."); return; }
      const q = Number(l.opening_qty);
      if (!Number.isFinite(q) || q < 1 || Math.floor(q) !== q) {
        setErr("Every opening quantity must be a positive whole number.");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post("/admin/backorders", {
        ap_user_id: apUserId,
        lines: lines.map((l) => ({
          product_id: l.product_id.trim().toUpperCase(),
          opening_qty: Number(l.opening_qty),
        })),
      });
      onDone();
    } catch (ex) {
      setErr(parseErrorDetail(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" data-testid="bo-create-modal">
      <div className="apwo-modal-card apwo-cat-modal-card">
        <header className="apwo-modal-head">
          <h3 className="apwo-modal-title">Create legacy backorder</h3>
          <button type="button" className="apwo-modal-close" onClick={onClose} data-testid="bo-create-close">×</button>
        </header>
        <div className="apwo-modal-body apwo-cat-modal-body">
          <form onSubmit={submit} data-testid="bo-create-form" noValidate>
            <label className="apwo-field">
              <span>AP account</span>
              <select value={apUserId} onChange={(e) => setApUserId(e.target.value)} required data-testid="bo-create-ap">
                <option value="" disabled>Select…</option>
                {apUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name} · {u.username}</option>
                ))}
              </select>
            </label>
            {lines.map((l, i) => (
              <div key={i} className="apwo-grid-two" data-testid={`bo-create-line-${i}`}>
                <label className="apwo-field">
                  <span>Product</span>
                  <select value={l.product_id} onChange={(e) => updateLine(i, "product_id", e.target.value)} required data-testid={`bo-create-product-${i}`}>
                    <option value="" disabled>Select…</option>
                    {products.map((p) => (
                      <option key={p.product_id} value={p.product_id}>{p.product_id} — {p.display_name}</option>
                    ))}
                  </select>
                </label>
                <label className="apwo-field">
                  <span>Opening qty (units)</span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="number" min="1" step="1" value={l.opening_qty}
                           onChange={(e) => updateLine(i, "opening_qty", e.target.value)}
                           required data-testid={`bo-create-qty-${i}`} />
                    {lines.length > 1 && (
                      <button type="button" className="apwo-btn-secondary apwo-btn-sm"
                              onClick={() => removeLine(i)} aria-label="Remove line"
                              data-testid={`bo-create-remove-${i}`}>–</button>
                    )}
                  </div>
                </label>
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <button type="button" className="apwo-btn-secondary apwo-btn-sm" onClick={addLine} data-testid="bo-create-add-line">
                + Add product line
              </button>
            </div>
            {err && <div className="apwo-alert-error" data-testid="bo-create-error" style={{ marginTop: 10 }}>{err}</div>}
            <div className="apwo-modal-actions">
              <button type="button" className="apwo-btn-secondary" onClick={onClose} disabled={busy} data-testid="bo-create-cancel">Cancel</button>
              <button type="submit" className="apwo-btn-primary" disabled={busy} data-testid="bo-create-submit">
                {busy ? "Saving…" : "Save opening balance"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Record delivery modal
function RecordDeliveryModal({ backorder, onClose, onDone }) {
  const [deliveryDate, setDeliveryDate] = useState(todayIso());
  const [xeroRef, setXeroRef] = useState("");
  const [items, setItems] = useState(
    (backorder.lines || []).filter((l) => l.remaining_qty > 0)
      .map((l) => ({ product_id: l.product_id, product_display_name: l.product_display_name,
                     max: l.remaining_qty, delivered_qty: "" })),
  );
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [idempKey] = useState(() => `deliv:${backorder.id}:${uuid()}`);

  const update = (i, v) => setItems((prev) =>
    prev.map((it, ix) => (ix === i ? { ...it, delivered_qty: v } : it))
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!deliveryDate) { setErr("Choose a delivery date."); return; }
    if (!xeroRef.trim()) { setErr("Enter the Xero invoice reference."); return; }
    const payloadItems = [];
    for (const it of items) {
      const raw = String(it.delivered_qty).trim();
      if (raw === "") continue;
      const q = Number(raw);
      if (!Number.isFinite(q) || q < 1 || Math.floor(q) !== q) {
        setErr(`Delivered qty for ${it.product_id} must be a positive whole number.`); return;
      }
      if (q > it.max) {
        setErr(`Delivered qty for ${it.product_id} (${q}) exceeds remaining (${it.max}).`); return;
      }
      payloadItems.push({ product_id: it.product_id, delivered_qty: q });
    }
    if (!payloadItems.length) { setErr("Enter at least one delivered quantity."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/backorders/${backorder.id}/deliveries`, {
        delivery_date: deliveryDate,
        xero_ref: xeroRef.trim(),
        items: payloadItems,
        idempotency_key: idempKey,
      });
      onDone();
    } catch (ex) {
      setErr(parseErrorDetail(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" data-testid="bo-delivery-modal">
      <div className="apwo-modal-card apwo-cat-modal-card">
        <header className="apwo-modal-head">
          <h3 className="apwo-modal-title">Record delivery</h3>
          <button type="button" className="apwo-modal-close" onClick={onClose} data-testid="bo-delivery-close">×</button>
        </header>
        <div className="apwo-modal-body apwo-cat-modal-body">
          <form onSubmit={submit} data-testid="bo-delivery-form" noValidate>
            <div className="apwo-grid-two">
              <label className="apwo-field">
                <span>Delivery date</span>
                <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}
                       required data-testid="bo-delivery-date" />
              </label>
              <label className="apwo-field">
                <span>Xero invoice reference</span>
                <input type="text" value={xeroRef} onChange={(e) => setXeroRef(e.target.value)}
                       placeholder="INV-1234" required maxLength={64} data-testid="bo-delivery-xero" />
              </label>
            </div>
            <p className="apwo-muted" style={{ marginTop: 8 }}>
              Enter delivered qty against one or more product lines below. Leave a line blank if
              nothing was delivered for that product in this shipment.
            </p>
            <div style={{ overflowX: "auto" }}>
              <table className="apwo-table" data-testid="bo-delivery-items">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="apwo-th-right">Remaining</th>
                    <th className="apwo-th-right">Delivered qty</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.product_id}>
                      <td>{it.product_display_name}<div className="apwo-cell-muted">{it.product_id}</div></td>
                      <td className="apwo-th-right">{it.max}</td>
                      <td className="apwo-th-right">
                        <input type="number" min="0" max={it.max} step="1"
                               value={it.delivered_qty}
                               onChange={(e) => update(i, e.target.value)}
                               className="apwo-qty-input apwo-qty-input--sm"
                               data-testid={`bo-delivery-qty-${it.product_id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {err && <div className="apwo-alert-error" data-testid="bo-delivery-error" style={{ marginTop: 10 }}>{err}</div>}
            <div className="apwo-modal-actions">
              <button type="button" className="apwo-btn-secondary" onClick={onClose} disabled={busy} data-testid="bo-delivery-cancel">Cancel</button>
              <button type="submit" className="apwo-btn-primary" disabled={busy} data-testid="bo-delivery-submit">
                {busy ? "Saving…" : "Record delivery"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Correction modal
function CorrectionModal({ backorder, onClose, onDone }) {
  const [pid, setPid] = useState(backorder.lines[0]?.product_id || "");
  const [newQty, setNewQty] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const line = useMemo(
    () => backorder.lines.find((l) => l.product_id === pid) || null,
    [pid, backorder.lines]
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const q = Number(newQty);
    if (!pid) { setErr("Pick a product."); return; }
    if (!Number.isFinite(q) || q < 1 || Math.floor(q) !== q) { setErr("New opening qty must be a positive whole number."); return; }
    if (reason.trim().length < 3) { setErr("Reason must be at least 3 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/backorders/${backorder.id}/corrections`, {
        product_id: pid, new_opening_qty: q, reason: reason.trim(),
      });
      onDone();
    } catch (ex) {
      setErr(parseErrorDetail(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" data-testid="bo-correction-modal">
      <div className="apwo-modal-card apwo-cat-modal-card">
        <header className="apwo-modal-head">
          <h3 className="apwo-modal-title">Correct opening quantity</h3>
          <button type="button" className="apwo-modal-close" onClick={onClose} data-testid="bo-correction-close">×</button>
        </header>
        <div className="apwo-modal-body apwo-cat-modal-body">
          <form onSubmit={submit} data-testid="bo-correction-form" noValidate>
            <label className="apwo-field">
              <span>Product</span>
              <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="bo-correction-product">
                {backorder.lines.map((l) => (
                  <option key={l.product_id} value={l.product_id}>
                    {l.product_id} — {l.product_display_name}
                  </option>
                ))}
              </select>
            </label>
            <p className="apwo-muted">Current opening: <strong>{line?.backorder_qty}</strong>. Already delivered: <strong>{line?.delivered_qty}</strong>. The new opening qty cannot be less than the already-delivered qty.</p>
            <label className="apwo-field">
              <span>New opening qty</span>
              <input type="number" min={line?.delivered_qty ?? 1} step="1" value={newQty}
                     onChange={(e) => setNewQty(e.target.value)} required data-testid="bo-correction-new-qty" />
            </label>
            <label className="apwo-field">
              <span>Reason (recorded, audited, non-secret)</span>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} minLength={3}
                     maxLength={240} required data-testid="bo-correction-reason" />
            </label>
            {err && <div className="apwo-alert-error" data-testid="bo-correction-error" style={{ marginTop: 10 }}>{err}</div>}
            <div className="apwo-modal-actions">
              <button type="button" className="apwo-btn-secondary" onClick={onClose} disabled={busy} data-testid="bo-correction-cancel">Cancel</button>
              <button type="submit" className="apwo-btn-primary" disabled={busy} data-testid="bo-correction-submit">
                {busy ? "Saving…" : "Save correction"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Reverse modal
function ReverseModal({ backorderId, delivery, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (reason.trim().length < 3) { setErr("Reason must be at least 3 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/backorders/${backorderId}/deliveries/${delivery.delivery_id}/reverse`,
        { reason: reason.trim() });
      onDone();
    } catch (ex) {
      setErr(parseErrorDetail(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="apwo-modal-overlay" role="dialog" aria-modal="true" data-testid="bo-reverse-modal">
      <div className="apwo-modal-card">
        <header className="apwo-modal-head">
          <h3 className="apwo-modal-title">Reverse delivery</h3>
          <button type="button" className="apwo-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="apwo-modal-body">
          <form onSubmit={submit}>
            <p className="apwo-muted">
              Reversing this delivery inserts a compensating record and reopens any completed
              lines. The original record is preserved (audited, never deleted).
            </p>
            <label className="apwo-field">
              <span>Reason</span>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                     minLength={3} maxLength={240} required data-testid="bo-reverse-reason" />
            </label>
            {err && <div className="apwo-alert-error" data-testid="bo-reverse-error" style={{ marginTop: 10 }}>{err}</div>}
            <div className="apwo-modal-actions">
              <button type="button" className="apwo-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="apwo-btn-primary" disabled={busy} data-testid="bo-reverse-submit">
                {busy ? "Reversing…" : "Confirm reversal"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Detail view
function BackorderDetail({ backorderId, onClose, reload }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);   // "delivery" | "correction" | {reverse: d}
  const load = async () => {
    try {
      const r = await api.get(`/admin/backorders/${backorderId}`);
      setDetail(r.data);
    } catch (ex) { setErr(parseErrorDetail(ex)); }
  };
  useEffect(() => { load(); }, [backorderId]);

  const onDone = async () => { setModal(null); await load(); reload && reload(); };

  return (
    <section className="apwo-panel" data-testid={`bo-detail-${backorderId}`} style={{ marginTop: 20 }}>
      <header className="apwo-catalogue-toolbar">
        <div>
          <h2 style={{ margin: 0 }}>Backorder detail</h2>
          <p className="apwo-muted" style={{ marginTop: 4 }}>
            {detail ? `AP: ${detail.ap_user_id} · State: ${detail.state}` : "Loading…"}
          </p>
        </div>
        <div className="apwo-cat-actions">
          {detail && detail.state === "ACTIVE" && (
            <>
              <button className="apwo-btn-primary" onClick={() => setModal("delivery")} data-testid="bo-open-delivery">Record delivery</button>
              <button className="apwo-btn-secondary" onClick={() => setModal("correction")} data-testid="bo-open-correction">Correct opening qty</button>
            </>
          )}
          <button className="apwo-btn-secondary" onClick={onClose} data-testid="bo-detail-close">Close</button>
        </div>
      </header>
      {err && <div className="apwo-alert-error" data-testid="bo-detail-error">{err}</div>}
      {detail && (
        <>
          <h3>Lines</h3>
          <div style={{ overflowX: "auto" }}>
            <table className="apwo-table apwo-cat-table" data-testid="bo-detail-lines">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="apwo-th-right">Backorder Qty</th>
                  <th className="apwo-th-right">Delivered Qty</th>
                  <th className="apwo-th-right">Remaining Qty</th>
                  <th>Latest Delivery Date</th>
                  <th>Latest Xero Ref</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.product_id} data-testid={`bo-detail-row-${l.product_id}`}>
                    <td>{l.product_display_name}<div className="apwo-cell-muted">{l.product_id}</div></td>
                    <td className="apwo-th-right">{l.backorder_qty}</td>
                    <td className="apwo-th-right">{l.delivered_qty}</td>
                    <td className="apwo-th-right"><strong>{l.remaining_qty}</strong></td>
                    <td>{l.latest_delivery_date || "—"}</td>
                    <td>{l.latest_xero_ref || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ marginTop: 24 }}>Delivery history</h3>
          {!detail.deliveries.length && <p className="apwo-muted">No deliveries yet.</p>}
          {detail.deliveries.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="apwo-table apwo-cat-table" data-testid="bo-detail-history">
                <thead>
                  <tr>
                    <th>Delivery Date</th><th>Xero Ref</th><th>Product</th>
                    <th className="apwo-th-right">Delivered Qty</th>
                    <th className="apwo-th-right">Remaining After</th>
                    <th>State</th><th className="apwo-th-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.deliveries.flatMap((d) =>
                    d.items.map((it, i) => (
                      <tr key={`${d.delivery_id}-${it.product_id}-${i}`}
                          className={d.state !== "ACTIVE" ? "apwo-oos-row" : ""}
                          data-testid={`bo-hist-${d.delivery_id}-${it.product_id}`}>
                        {i === 0 ? <>
                          <td rowSpan={d.items.length}>{d.delivery_date}</td>
                          <td rowSpan={d.items.length}><code>{d.xero_ref}</code></td>
                        </> : null}
                        <td>{it.product_display_name}<div className="apwo-cell-muted">{it.product_id}</div></td>
                        <td className="apwo-th-right">{it.delivered_qty}</td>
                        <td className="apwo-th-right">{it.remaining_after}</td>
                        {i === 0 ? <>
                          <td rowSpan={d.items.length}>
                            <span className={`apwo-cat-status-pill ${d.state === "ACTIVE" ? "apwo-cat-status-pill--in" : "apwo-cat-status-pill--out"}`}>
                              {d.state}
                            </span>
                          </td>
                          <td rowSpan={d.items.length} className="apwo-th-right">
                            {d.state === "ACTIVE" && detail.state === "ACTIVE" && (
                              <button className="apwo-btn-secondary apwo-btn-sm"
                                      onClick={() => setModal({ reverse: d })}
                                      data-testid={`bo-reverse-${d.delivery_id}`}>Reverse</button>
                            )}
                          </td>
                        </> : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {detail.corrections?.length > 0 && (
            <>
              <h3 style={{ marginTop: 24 }}>Corrections</h3>
              <div style={{ overflowX: "auto" }}>
                <table className="apwo-table" data-testid="bo-detail-corrections">
                  <thead>
                    <tr><th>When</th><th>Product</th><th className="apwo-th-right">Old qty</th><th className="apwo-th-right">New qty</th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    {detail.corrections.map((c) => (
                      <tr key={c.id}>
                        <td>{c.ts?.slice(0, 19).replace("T", " ")}</td>
                        <td>{c.product_id}</td>
                        <td className="apwo-th-right">{c.old_opening_qty}</td>
                        <td className="apwo-th-right">{c.new_opening_qty}</td>
                        <td>{c.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
      {modal === "delivery" && detail && (
        <RecordDeliveryModal backorder={detail} onClose={() => setModal(null)} onDone={onDone} />
      )}
      {modal === "correction" && detail && (
        <CorrectionModal backorder={detail} onClose={() => setModal(null)} onDone={onDone} />
      )}
      {modal && modal.reverse && (
        <ReverseModal backorderId={backorderId} delivery={modal.reverse}
                      onClose={() => setModal(null)} onDone={onDone} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Page
export default function AdminBackorders() {
  const { user, logout } = useAuth();
  const [list, setList] = useState(null);
  const [apUsers, setApUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    try {
      const [bo, users, prods] = await Promise.all([
        api.get("/admin/backorders"),
        api.get("/admin/users"),
        api.get("/admin/catalogue/products"),
      ]);
      setList(bo.data);
      setApUsers((users.data || []).filter((u) => u.role && u.role !== "OWNER_ADMIN"));
      setProducts(prods.data || []);
    } catch (ex) { setErr(parseErrorDetail(ex)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="apwo-shell apwo-shell--admin">
      <header className="apwo-shell-header">
        <p className="apwo-brand-eyebrow">AUTOPARTSB2B</p>
        <h1 className="apwo-shell-title">Admin — Backorders</h1>
        <div className="apwo-shell-actions">
          <span className="apwo-user-chip">{user?.display_name} <span className="apwo-role-tag">{user?.role}</span></span>
          <button className="apwo-btn-secondary" onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>
      </header>
      <main className="apwo-shell-main">
        <nav className="apwo-nav-tabs" aria-label="Admin sections">
          <Link to="/admin" className="apwo-nav-tab" data-testid="admin-nav-users">Users & Overview</Link>
          <Link to="/admin/orders" className="apwo-nav-tab" data-testid="admin-nav-orders">Orders Inbox</Link>
          <Link to="/admin/catalogue" className="apwo-nav-tab" data-testid="admin-nav-catalogue">Catalogue</Link>
          <Link to="/admin/backorders" className="apwo-nav-tab active" data-testid="admin-nav-backorders">Backorders</Link>
        </nav>

        <section className="apwo-panel">
          <header className="apwo-catalogue-toolbar">
            <div>
              <h2 style={{ margin: 0 }}>Legacy backorders</h2>
              <p className="apwo-muted" style={{ marginTop: 4 }}>
                Quantity-only monitoring for pre-portal AP orders. No pricing / GST /
                payment / Reward / catalogue-stock / order-numbers created here.
              </p>
            </div>
            <button className="apwo-btn-primary" onClick={() => setShowCreate(true)} data-testid="bo-open-create">Create backorder</button>
          </header>
          {err && <div className="apwo-alert-error" data-testid="bo-list-error">{err}</div>}
          {!list && !err && <p className="apwo-muted">Loading…</p>}
          {list && (
            <>
              <h3 style={{ margin: "16px 0 8px" }}>Open ({list.open.length})</h3>
              {!list.open.length && <p className="apwo-muted" data-testid="bo-open-empty">No open backorders.</p>}
              {list.open.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table className="apwo-table apwo-cat-table" data-testid="bo-open-table">
                    <thead>
                      <tr><th>AP account</th><th className="apwo-th-right">Lines</th><th className="apwo-th-right">Total remaining</th><th>Created</th><th className="apwo-th-right">Action</th></tr>
                    </thead>
                    <tbody>
                      {list.open.map((bo) => {
                        const remaining = bo.lines.reduce((s, l) => s + l.remaining_qty, 0);
                        return (
                          <tr key={bo.id} data-testid={`bo-open-row-${bo.id}`}>
                            <td>{bo.ap_user_id}</td>
                            <td className="apwo-th-right">{bo.lines.length}</td>
                            <td className="apwo-th-right"><strong>{remaining}</strong></td>
                            <td>{bo.created_at?.slice(0, 10)}</td>
                            <td className="apwo-th-right">
                              <button className="apwo-btn-secondary apwo-btn-sm" onClick={() => setSelectedId(bo.id)} data-testid={`bo-open-view-${bo.id}`}>Open</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <h3 style={{ margin: "24px 0 8px" }}>Completed history ({list.completed.length})</h3>
              {!list.completed.length && <p className="apwo-muted" data-testid="bo-completed-empty">No completed backorders yet.</p>}
              {list.completed.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table className="apwo-table apwo-cat-table" data-testid="bo-completed-table">
                    <thead>
                      <tr><th>AP account</th><th className="apwo-th-right">Lines</th><th>Completed</th><th className="apwo-th-right">Action</th></tr>
                    </thead>
                    <tbody>
                      {list.completed.map((bo) => (
                        <tr key={bo.id} data-testid={`bo-comp-row-${bo.id}`}>
                          <td>{bo.ap_user_id}</td>
                          <td className="apwo-th-right">{bo.lines.length}</td>
                          <td>{bo.completed_at?.slice(0, 10) || "—"}</td>
                          <td className="apwo-th-right">
                            <button className="apwo-btn-secondary apwo-btn-sm" onClick={() => setSelectedId(bo.id)} data-testid={`bo-comp-view-${bo.id}`}>Open</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        {selectedId && (
          <BackorderDetail backorderId={selectedId}
                           onClose={() => setSelectedId(null)}
                           reload={load} />
        )}
      </main>
      <footer className="apwo-shell-footer">
        AUTOPARTSB2B &middot; S1 Asia Pacific Traders Pty Ltd &middot; ABN 69 650 366 091
      </footer>
      {showCreate && (
        <CreateBackorderModal apUsers={apUsers} products={products}
                              onClose={() => setShowCreate(false)}
                              onDone={async () => { setShowCreate(false); await load(); }} />
      )}
    </div>
  );
}
